"use client";

import { c as _c } from "react/compiler-runtime";
import DropListItemContentMedia from "@/components/drops/view/item/content/media/DropListItemContentMedia";
import { MobileVotingModal, VotingModal } from "@/components/voting";
import VotingModalButton from "@/components/voting/VotingModalButton";
import { useVotingModalState } from "@/components/voting/useVotingModalState";
import DropMobileMenuHandler from "@/components/waves/drops/DropMobileMenuHandler";
import { AdditionalActionPromiseBadge } from "@/components/waves/drops/AdditionalActionPromiseBadge";
import { getRankHoverBorderClass } from "@/components/waves/drops/dropRankStyles";
import WaveDropReactions from "@/components/waves/drops/WaveDropReactions";
import type { DropInteractionParams } from "@/components/waves/drops/drop.types";
import { DropLocation, hasDropFooter } from "@/components/waves/drops/drop.types";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { useDropInteractionRules } from "@/hooks/drops/useDropInteractionRules";
import useIsMobileDevice from "@/hooks/isMobileDevice";
import useIsMobileScreen from "@/hooks/isMobileScreen";
import type { ActiveDropState } from "@/types/dropInteractionTypes";
import { useCallback, type MouseEvent, type ReactNode } from "react";
import MemeDropActions from "./meme-participation-drop/MemeDropActions";
import MemeDropArtistInfo from "./meme-participation-drop/MemeDropArtistInfo";
import MemeDropDescription from "./meme-participation-drop/MemeDropDescription";
import MemeDropHeader from "./meme-participation-drop/MemeDropHeader";
import MemeDropVoteStats from "./meme-participation-drop/MemeDropVoteStats";
import MemeDropTraits from "./MemeDropTraits";
interface MemeParticipationDropProps {
  readonly drop: ExtendedDrop;
  readonly activeDrop: ActiveDropState | null;
  readonly showReplyAndQuote: boolean;
  readonly location: DropLocation;
  readonly onReply: (param: DropInteractionParams) => void;
  readonly onDropContentClick?: ((drop: ExtendedDrop) => void) | undefined;
  readonly footer?: ReactNode;
  readonly showInteractions?: boolean | undefined;
  readonly isVotingClosed?: boolean | undefined;
  readonly isVotingControlsLocked?: boolean | undefined;
}
const getNonEmptyText = (value: string | null | undefined): string | null => {
  const text = value?.trim();
  return text && text.length > 0 ? text : null;
};
const getMetadataValue = (drop: ExtendedDrop, dataKey: string): string | null => getNonEmptyText(drop.metadata.find(metadata => metadata.data_key === dataKey)?.data_value);

// Border styling based on rank
const getBorderClasses = (drop: ExtendedDrop, isActiveDrop: boolean) => {
  const rank = typeof drop.rank === "number" && drop.rank <= 3 ? drop.rank : null;
  const baseClasses = "tw-rounded-xl tw-border tw-border-solid tw-border-iron-800 tw-transition-all tw-duration-200 tw-ease-out tw-overflow-hidden";
  if (isActiveDrop) {
    return `${baseClasses} desktop-hover:hover:tw-border-[#3CCB7F]/40 tw-bg-[#3CCB7F]/5`;
  }
  if (rank === 1) {
    return `${baseClasses} ${getRankHoverBorderClass(1)}`;
  }
  if (rank === 2) {
    return `${baseClasses} ${getRankHoverBorderClass(2)}`;
  }
  if (rank === 3) {
    return `${baseClasses} ${getRankHoverBorderClass(3)}`;
  }
  return `${baseClasses} tw-border-iron-800`;
};
export default function MemeParticipationDrop(t0) {
  const $ = _c(88);
  const {
    drop,
    activeDrop,
    showReplyAndQuote,
    location,
    onReply,
    onDropContentClick,
    footer,
    showInteractions: t1,
    isVotingClosed: t2,
    isVotingControlsLocked: t3
  } = t0;
  const showInteractions = t1 === undefined ? true : t1;
  const isVotingClosed = t2 === undefined ? false : t2;
  const isVotingControlsLocked = t3 === undefined ? false : t3;
  const {
    canShowVote
  } = useDropInteractionRules(drop);
  const isVotingActionLocked = isVotingClosed || isVotingControlsLocked;
  const {
    isOpen: isVotingModalOpen,
    open: openVotingModal,
    close: closeVotingModal
  } = useVotingModalState(isVotingActionLocked);
  const isActiveDrop = activeDrop?.drop.id === drop.id;
  const isMobile = useIsMobileDevice();
  const isMobileScreen = useIsMobileScreen();
  let description;
  let t4;
  let title;
  if ($[0] !== drop) {
    const firstPart = drop.parts.at(0);
    title = getNonEmptyText(drop.title) ?? getMetadataValue(drop, "title") ?? "Artwork Title";
    description = getNonEmptyText(firstPart?.content) ?? getMetadataValue(drop, "description") ?? "This is an artwork submission for The Memes collection.";
    t4 = firstPart?.media.at(0);
    $[0] = drop;
    $[1] = description;
    $[2] = t4;
    $[3] = title;
  } else {
    description = $[1];
    t4 = $[2];
    title = $[3];
  }
  const artworkMedia = t4;
  let t5;
  if ($[4] !== drop || $[5] !== isActiveDrop) {
    t5 = getBorderClasses(drop, isActiveDrop);
    $[4] = drop;
    $[5] = isActiveDrop;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const borderClasses = t5;
  let t6;
  if ($[7] !== drop || $[8] !== onReply) {
    t6 = () => {
      onReply({
        drop,
        partId: drop.parts[0]?.part_id
      });
    };
    $[7] = drop;
    $[8] = onReply;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const handleOnReply = t6;
  let t7;
  if ($[10] !== drop.id || $[11] !== onDropContentClick) {
    t7 = !drop.id.startsWith("temp-") && !!onDropContentClick;
    $[10] = drop.id;
    $[11] = onDropContentClick;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const isContentInteractive = t7;
  let t8;
  if ($[13] !== title) {
    t8 = <MemeDropHeader title={title} />;
    $[13] = title;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== drop.is_additional_action_promised) {
    t9 = drop.is_additional_action_promised === true && <AdditionalActionPromiseBadge />;
    $[15] = drop.is_additional_action_promised;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== t8 || $[18] !== t9) {
    t10 = <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">{t8}{t9}</div>;
    $[17] = t8;
    $[18] = t9;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  const headerContent = t10;
  let t11;
  if ($[20] !== drop || $[21] !== isContentInteractive || $[22] !== onDropContentClick) {
    t11 = event => {
      const selection = globalThis.getSelection?.() ?? null;
      if (selection?.toString()) {
        return;
      }
      if (!isContentInteractive || !onDropContentClick) {
        return;
      }
      event.stopPropagation();
      onDropContentClick(drop);
    };
    $[20] = drop;
    $[21] = isContentInteractive;
    $[22] = onDropContentClick;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  const handleContentClick = t11;
  let t12;
  if ($[24] !== drop) {
    t12 = <MemeDropArtistInfo drop={drop} />;
    $[24] = drop;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== description || $[27] !== handleContentClick || $[28] !== headerContent || $[29] !== isContentInteractive) {
    t13 = isContentInteractive ? <button className="tw-mt-2 tw-block tw-w-full tw-cursor-pointer tw-border-0 tw-bg-transparent tw-p-0 tw-text-left tw-text-inherit focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-offset-2 focus-visible:tw-outline-primary-400 sm:tw-ml-[3.25rem] sm:tw-mt-1.5 sm:tw-w-auto" onClick={handleContentClick} type="button">{headerContent}<MemeDropDescription description={description} /></button> : <div className="tw-mt-2 tw-flex tw-flex-col sm:tw-ml-[3.25rem] sm:tw-mt-1.5">{headerContent}<MemeDropDescription description={description} /></div>;
    $[26] = description;
    $[27] = handleContentClick;
    $[28] = headerContent;
    $[29] = isContentInteractive;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] !== t12 || $[32] !== t13) {
    t14 = <div className="tw-p-4">{t12}{t13}</div>;
    $[31] = t12;
    $[32] = t13;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  let t15;
  if ($[34] !== artworkMedia || $[35] !== location) {
    t15 = artworkMedia && <div className={`tw-flex tw-h-96 tw-justify-center ${location === DropLocation.WAVE ? "tw-bg-iron-800/30" : "tw-bg-iron-900/40"}`}><DropListItemContentMedia media_mime_type={artworkMedia.mime_type} media_url={artworkMedia.url} isCompetitionDrop={true} /></div>;
    $[34] = artworkMedia;
    $[35] = location;
    $[36] = t15;
  } else {
    t15 = $[36];
  }
  let t16;
  if ($[37] !== drop) {
    t16 = <div className="tw-px-4 tw-py-4"><MemeDropTraits drop={drop} /></div>;
    $[37] = drop;
    $[38] = t16;
  } else {
    t16 = $[38];
  }
  let t17;
  if ($[39] !== t14 || $[40] !== t15 || $[41] !== t16) {
    const contentBody = <>{t14}{t15}{t16}</>;
    t17 = <div>{contentBody}</div>;
    $[39] = t14;
    $[40] = t15;
    $[41] = t16;
    $[42] = t17;
  } else {
    t17 = $[42];
  }
  const content = t17;
  const t18 = `tw-group tw-relative tw-w-full ${location === DropLocation.WAVE ? "tw-px-4 tw-py-1" : ""}`;
  const t19 = `${borderClasses} ${location === DropLocation.WAVE ? "tw-bg-iron-900/80" : "tw-bg-iron-950"}`;
  let t20;
  if ($[43] !== content || $[44] !== drop || $[45] !== handleOnReply || $[46] !== showInteractions || $[47] !== showReplyAndQuote) {
    t20 = showInteractions ? <DropMobileMenuHandler drop={drop} showReplyAndQuote={showReplyAndQuote} onReply={handleOnReply}>{content}</DropMobileMenuHandler> : content;
    $[43] = content;
    $[44] = drop;
    $[45] = handleOnReply;
    $[46] = showInteractions;
    $[47] = showReplyAndQuote;
    $[48] = t20;
  } else {
    t20 = $[48];
  }
  let t21;
  if ($[49] !== drop) {
    t21 = <div className="tw-px-4 @[700px]:tw-px-0"><MemeDropVoteStats drop={drop} /></div>;
    $[49] = drop;
    $[50] = t21;
  } else {
    t21 = $[50];
  }
  let t22;
  if ($[51] !== canShowVote || $[52] !== drop || $[53] !== isVotingActionLocked || $[54] !== openVotingModal || $[55] !== showInteractions) {
    t22 = canShowVote && showInteractions && !isVotingActionLocked && <div className="tw-flex tw-w-full tw-justify-center tw-border-x-0 tw-border-b-0 tw-border-t tw-border-solid tw-border-iron-800 tw-px-6 tw-pt-4 @[700px]:tw-w-auto @[700px]:tw-border-none @[700px]:tw-px-0 @[700px]:tw-pt-0"><div onClick={_temp}><VotingModalButton drop={drop} onClick={openVotingModal} /></div></div>;
    $[51] = canShowVote;
    $[52] = drop;
    $[53] = isVotingActionLocked;
    $[54] = openVotingModal;
    $[55] = showInteractions;
    $[56] = t22;
  } else {
    t22 = $[56];
  }
  let t23;
  if ($[57] !== t21 || $[58] !== t22) {
    t23 = <div className="tw-flex tw-flex-col tw-justify-between tw-gap-3 tw-pb-4 @[700px]:tw-flex-row @[700px]:tw-items-center @[700px]:tw-gap-y-0 @[700px]:tw-px-4">{t21}{t22}</div>;
    $[57] = t21;
    $[58] = t22;
    $[59] = t23;
  } else {
    t23 = $[59];
  }
  let t24;
  if ($[60] !== drop || $[61] !== showInteractions) {
    t24 = showInteractions && <div className="tw-flex tw-w-full tw-flex-wrap tw-items-center tw-gap-x-2 tw-gap-y-1 tw-px-4 tw-pb-4"><WaveDropReactions drop={drop} /></div>;
    $[60] = drop;
    $[61] = showInteractions;
    $[62] = t24;
  } else {
    t24 = $[62];
  }
  let t25;
  if ($[63] !== footer) {
    t25 = hasDropFooter(footer) && <div className="tw-px-4 tw-pb-4 tw-pt-2">{footer}</div>;
    $[63] = footer;
    $[64] = t25;
  } else {
    t25 = $[64];
  }
  let t26;
  if ($[65] !== drop || $[66] !== handleOnReply || $[67] !== isMobile || $[68] !== showInteractions || $[69] !== showReplyAndQuote) {
    t26 = showInteractions && <div className="tw-absolute tw-right-4 tw-top-2"><MemeDropActions drop={drop} isMobile={isMobile} showReplyAndQuote={showReplyAndQuote} onReply={handleOnReply} /></div>;
    $[65] = drop;
    $[66] = handleOnReply;
    $[67] = isMobile;
    $[68] = showInteractions;
    $[69] = showReplyAndQuote;
    $[70] = t26;
  } else {
    t26 = $[70];
  }
  let t27;
  if ($[71] !== t19 || $[72] !== t20 || $[73] !== t23 || $[74] !== t24 || $[75] !== t25 || $[76] !== t26) {
    t27 = <div className={t19}>{t20}{t23}{t24}{t25}{t26}</div>;
    $[71] = t19;
    $[72] = t20;
    $[73] = t23;
    $[74] = t24;
    $[75] = t25;
    $[76] = t26;
    $[77] = t27;
  } else {
    t27 = $[77];
  }
  let t28;
  if ($[78] !== closeVotingModal || $[79] !== drop || $[80] !== isMobileScreen || $[81] !== isVotingModalOpen || $[82] !== showInteractions) {
    t28 = showInteractions && (isMobileScreen ? <MobileVotingModal drop={drop} isOpen={isVotingModalOpen} onClose={closeVotingModal} /> : <VotingModal drop={drop} isOpen={isVotingModalOpen} onClose={closeVotingModal} />);
    $[78] = closeVotingModal;
    $[79] = drop;
    $[80] = isMobileScreen;
    $[81] = isVotingModalOpen;
    $[82] = showInteractions;
    $[83] = t28;
  } else {
    t28 = $[83];
  }
  let t29;
  if ($[84] !== t18 || $[85] !== t27 || $[86] !== t28) {
    t29 = <div className="tw-w-full tw-@container"><div className={t18}>{t27}{t28}</div></div>;
    $[84] = t18;
    $[85] = t27;
    $[86] = t28;
    $[87] = t29;
  } else {
    t29 = $[87];
  }
  return t29;
}
function _temp(e) {
  return e.stopPropagation();
}